import React from 'react';
import { useTranslation } from 'react-i18next';
import './Pagination.css';

interface PaginationProps {
    /** 1-based current page. */
    page: number;
    /** Total number of items across all pages. */
    totalItems: number;
    /** Items shown per page. */
    perPage: number;
    /** Fires when the user clicks a page button. */
    onPageChange: (page: number) => void;
    /** Fires when the items-per-page select changes. Hides the select when omitted. */
    onPerPageChange?: (perPage: number) => void;
    /** Options offered in the items-per-page select. */
    perPageOptions?: number[];
    /** Singular noun used in the "Showing X-Y of Z things" caption. */
    itemLabel?: string;
}

/**
 * Generate a compact page list with ellipses around the active page.
 * Example for current=7, total=15 → [1, '…', 6, 7, 8, '…', 15]
 */
const pageWindow = (
    current: number,
    total: number,
    siblings = 1
): Array<number | '…'> => {
    if (total <= 1) return [1];
    const first = 1;
    const last = total;
    const start = Math.max(current - siblings, first + 1);
    const end = Math.min(current + siblings, last - 1);

    const out: Array<number | '…'> = [first];
    if (start > first + 1) out.push('…');
    for (let p = start; p <= end; p++) out.push(p);
    if (end < last - 1) out.push('…');
    if (last !== first) out.push(last);
    return out;
};

const Pagination: React.FC<PaginationProps> = ({
    page,
    totalItems,
    perPage,
    onPageChange,
    onPerPageChange,
    perPageOptions = [10, 25, 50],
    itemLabel = 'item',
}) => {
    const { t } = useTranslation();
    const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const firstShown = totalItems === 0 ? 0 : (safePage - 1) * perPage + 1;
    const lastShown = Math.min(safePage * perPage, totalItems);

    const goto = (p: number) => {
        if (p < 1 || p > totalPages || p === safePage) return;
        onPageChange(p);
    };

    if (totalItems === 0) {
        return (
            <div className="gs-pagination gs-pagination--empty">
                <span className="gs-pagination__caption">
                    {t('common.noItems', { label: itemLabel })}
                </span>
            </div>
        );
    }

    const pages = pageWindow(safePage, totalPages);

    return (
        <div className="gs-pagination">
            <span className="gs-pagination__caption">
                {t('common.showing')} <strong>{firstShown}</strong>–<strong>{lastShown}</strong>{' '}
                {t('common.of')} <strong>{totalItems}</strong> {itemLabel}
                {totalItems === 1 ? '' : 's'}
            </span>

            <div className="gs-pagination__controls">
                {onPerPageChange && (
                    <label className="gs-pagination__perpage">
                        <span>{t('common.perPage')}</span>
                        <select
                            value={perPage}
                            onChange={(e) =>
                                onPerPageChange(Number(e.target.value))
                            }
                        >
                            {perPageOptions.map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                <div className="gs-pagination__pages">
                    <button
                        type="button"
                        className="gs-pagination__nav"
                        data-cy="pagination-prev"
                        onClick={() => goto(safePage - 1)}
                        disabled={safePage <= 1}
                        aria-label={t('common.prevPage')}
                    >
                        ‹
                    </button>

                    {pages.map((p, i) =>
                        p === '…' ? (
                            <span
                                key={`gap-${i}`}
                                className="gs-pagination__gap"
                                aria-hidden
                            >
                                …
                            </span>
                        ) : (
                            <button
                                key={p}
                                type="button"
                                className={`gs-pagination__page ${
                                    p === safePage
                                        ? 'gs-pagination__page--active'
                                        : ''
                                }`}
                                data-cy={`pagination-page-${p}`}
                                onClick={() => goto(p)}
                                aria-current={p === safePage ? 'page' : undefined}
                            >
                                {p}
                            </button>
                        )
                    )}

                    <button
                        type="button"
                        className="gs-pagination__nav"
                        data-cy="pagination-next"
                        onClick={() => goto(safePage + 1)}
                        disabled={safePage >= totalPages}
                        aria-label={t('common.nextPage')}
                    >
                        ›
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Pagination;
